import { CurrencyFormat, Throbber } from '@lt/components';
import { RefreshClockFill } from '@lt/components/icons-react';
import { InstantConfirm } from '@lt/components/icons-react/userflow';
import { useTranslation } from '@lt/localization';
import React, { useState } from 'react';
import { getRemainingTime } from 'src/utils/dateUtils';
import styled from 'styled-components';
import { useInterval } from 'usehooks-ts';
import { TEST_ID_PRICE_CHEKER_TIMER } from './constants';

type Props = {
  lastUpdated: number;
  timeout: number;
  fetching: boolean;
  instantConfirm: boolean;
  currentPrice?: number;
};

const PriceTimer = (props: Props) => {
  const { timeout, lastUpdated, fetching, currentPrice, instantConfirm } =
    props;
  const [remainingTime, setRemainingTime] = useState(
    lastUpdated + timeout - Date.now(),
  );

  const { t } = useTranslation();

  useInterval(() => {
    setRemainingTime(lastUpdated + timeout - Date.now());
  }, 1000);

  const actualizeTimeoutText =
    fetching || remainingTime < 0 ? (
      t('hotel.searcherTimer', { context: 'update' })
    ) : (
      <>
        {t('checkout.billInfo.priceChecker')}{' '}
        <RefreshClockIcon width={16} height={16} />
        {getRemainingTime(remainingTime)}
      </>
    );

  return (
    <Wrapper data-testid={TEST_ID_PRICE_CHEKER_TIMER}>
      {fetching || remainingTime < 0 ? (
        <StyledPreloader />
      ) : (
        <PriceInfo>
          {instantConfirm && <InstantConfirm width={10} height={20} />}
          {currentPrice && <CurrencyFormat value={currentPrice} />}
        </PriceInfo>
      )}
      <RemainingTime $hasPriceInfo={!!instantConfirm || !!currentPrice}>
        {actualizeTimeoutText}
      </RemainingTime>
    </Wrapper>
  );
};

const PriceInfo = styled.div`
  display: flex;
  font-weight: 700;
  font-size: 16px;

  > * + * {
    margin-left: 8px;
  }
`;

const StyledPreloader = styled(Throbber).attrs({
  embed: true,
  size: 16,
  color: '#4d4d4d',
})`
  width: 16px;
  height: 16px;
  margin: 0;
`;

const Wrapper = styled.div`
  display: flex;
  align-items: center;

  min-height: 20px;
`;

const RefreshClockIcon = styled(RefreshClockFill)`
  margin: 0 4px;
  fill: #f8e71c;
  stroke: #4d4d4d;
`;

const RemainingTime = styled.div<{ $hasPriceInfo: boolean }>`
  color: #4d4d4d;
  display: flex;
  align-items: center;
  ${({ $hasPriceInfo }) => $hasPriceInfo && ` margin-left: 8px;`}
`;

export default PriceTimer;
